import { createSelector } from "reselect";
import { getters } from "./model";
import { FILE_TYPES } from "./constants";
import { addS, capitalizeFirst } from "utils/General";
import moment from "moment";
import { canUserDo } from "redux/modules/permissions/user-permission-profile/selectors";
import { eventId as getEventId } from "redux/modules/event/selectors";
import * as STANDARD_MODULE_IDS from "@sessionboard/sessionboard-value-types/src/constants/standard-modules";

export const getIsValid = createSelector(getters.fileUploadData, data => {
  return !(!data.title || !data.title.length || !data.url || !data.url.length);
});

export const canAdminUserUploadContent = createSelector(
  getters.viaPortal,
  state => canUserDo(state, getEventId(state)),
  (viaPortal, canDo) =>
    viaPortal
      ? true
      : !viaPortal && canDo(`${STANDARD_MODULE_IDS.content.id}_create`)
);

export const canAdminUserEditContent = createSelector(
  getters.viaPortal,
  state => canUserDo(state, getEventId(state)),
  (viaPortal, canDo) =>
    viaPortal
      ? true
      : !viaPortal && canDo(`${STANDARD_MODULE_IDS.content.id}_edit`)
);

export const canAdminUserDeleteContent = createSelector(
  getters.viaPortal,
  state => canUserDo(state, getEventId(state)),
  (viaPortal, canDo) =>
    viaPortal
      ? true
      : !viaPortal && canDo(`${STANDARD_MODULE_IDS.content.id}_delete`)
);

export const getFilesList = createSelector(getters.content, content =>
  content.map(({ id, parent_content_id, title }) => ({
    id: parent_content_id ? parent_content_id : id,
    title
  }))
);

export const getFileProgress = createSelector(
  getters.fileUploadProgress,
  (_, props) => props.uploadId,
  (progress, uploadId) => {
    return progress[uploadId] ? progress[uploadId].totalPercent : 0;
  }
);

export const getEditTypeFile = createSelector(
  getters.showEditTypeModal,
  getters.combinedContent,
  (fileId, content) => content.find(c => c.id === fileId)
);

export const getUploadDescription = createSelector(
  getters.settings,
  settings => {
    return `Upload up to ${settings.content_limit} file${addS(
      settings.content_limit
    )} to attach to this session.${
      !settings.content_filetypes.includes("all") &&
      settings.content_filetypes.length
        ? ` Files will be limited to: ${settings.content_filetypes
            .map(f => capitalizeFirst(f))
            .join(", ")}.`
        : ""
    }${
      settings.content_due_date_formatted &&
      settings.content_due_date_formatted.length
        ? ` All content must be submitted by ${settings.content_due_date_formatted}.`
        : ""
    }`;
  }
);

export const getAllowedFileTypes = createSelector(
  getters.settings,
  settings => {
    return settings.content_filetypes;
  }
);

export const getCountOfMaxFilesToUpload = createSelector(
  getters.settings,
  getters.content,
  (settings, content) => {
    return settings.content_limit - content.length;
  }
);

export const getIsExpired = createSelector(
  getters.settings,
  getters.viaPortal,
  (settings, viaPortal) => {
    if (!viaPortal) {
      return false;
    }

    return !!(
      settings.content_due_date &&
      moment().isAfter(moment(settings.content_due_date))
    );
  }
);

export const getIsDisabled = createSelector(
  getters.settings,
  getters.viaPortal,
  (settings, viaPortal) => {
    if (!viaPortal) {
      return false;
    }

    return !settings.enable_content_upload;
  }
);

export const getEnableComments = createSelector(getters.settings, settings => {
  return settings.enable_comments;
});

export const getIsClosed = createSelector(
  getIsExpired,
  getIsDisabled,
  (expired, disabled) => expired || disabled
);

export const getSelectOptions = createSelector(getters.content, content => {
  return {
    type: {
      title: "Type",
      hasNew: false,
      textNew: undefined,
      valueNew: undefined,
      separator: "Presentation",
      options: FILE_TYPES.map(t => ({
        text: t.label,
        value: t.id
      }))
    },
    versions: {
      title: "Version",
      hasNew: true,
      textNew: "New File",
      valueNew: undefined,
      separator: "New version of:",
      options: content.map(c => ({
        text: c.title || "(Untitled File)",
        value: c.id
      }))
    }
  };
});
